<template>
  <div v-if="user" class="mr-4">
    <v-menu
      v-model="menuAberto"
      offset-y
      left
      max-width="380"
      min-width="340"
      :close-on-content-click="false"
      content-class="notif-panel"
    >
      <template v-slot:activator="{ on, attrs }">
        <div class="notification-wrapper">
          <v-badge :content="totalNaoLidas" :value="totalNaoLidas > 0" color="#ef4444" overlap>
            <v-btn
              icon
              class="notif-bell-btn"
              :class="{ 'bell-ring': totalNaoLidas > 0 }"
              v-bind="attrs"
              v-on="on"
            >
              <v-icon color="white">
                {{ totalNaoLidas > 0 ? "mdi-bell-ring" : "mdi-bell-outline" }}
              </v-icon>
            </v-btn>
          </v-badge>
        </div>
      </template>

      <v-card class="notif-card">
        <div class="notif-header">
          <span class="notif-header-title">
            <v-icon small class="mr-1" color="white">mdi-bell-ring-outline</v-icon>
            Notificações
          </span>
          <v-chip v-if="totalNaoLidas > 0" class="notif-count-chip" small>
            {{ totalNaoLidas }} nova{{ totalNaoLidas > 1 ? "s" : "" }}
          </v-chip>
        </div>
        <v-divider class="notif-header-divider" />

        <v-card-text v-if="carregando && notificacoes.length === 0" class="text-center py-6">
          <v-progress-circular indeterminate color="var(--brand-3)" size="26" width="2" />
        </v-card-text>

        <v-card-text v-else-if="notificacoes.length === 0" class="text-center py-10 notif-empty">
          <div class="notif-empty-icon">
            <v-icon x-large color="var(--brand-3)">mdi-bell-off-outline</v-icon>
          </div>
          <div class="notif-empty-text">Nenhuma notificação encontrada</div>
        </v-card-text>

        <v-list v-else class="pa-2 notif-list" dense>
          <v-list-item
            v-for="item in notificacoes"
            :key="item.id"
            :class="{ 'notif-item-unread': item.status_view === 'NAO_LIDA' }"
            class="notif-item px-2 py-2 align-start"
            @click="clicarNotificacao(item)"
          >
            <div v-if="item.status_view === 'NAO_LIDA'" class="notif-dot"></div>

            <div class="notif-icon" :class="notifIconCls(item.tipo)">
              <v-icon small color="white">{{ notifIcon(item.tipo) }}</v-icon>
            </div>

            <v-list-item-content class="pa-0 ml-3">
              <v-list-item-title class="font-weight-bold text-wrap text-body-2 notif-title mb-1">
                {{ item.titulo }}
              </v-list-item-title>
              <v-list-item-subtitle class="text-wrap text-caption notif-message mb-1">
                {{ item.mensagem }}
              </v-list-item-subtitle>
              <span class="text-caption notif-date">{{ formatarDataHora(item.created_at) }}</span>
            </v-list-item-content>

            <v-list-item-action class="ma-0 align-self-center ml-2">
              <v-btn icon small class="notif-delete-btn" @click.stop="excluirNotificacao(item.id)">
                <v-icon small color="var(--text-faint)">mdi-close</v-icon>
              </v-btn>
            </v-list-item-action>
          </v-list-item>
        </v-list>

        <v-divider />
        <v-card-actions class="py-2 px-3">
          <v-btn
            text
            x-small
            color="var(--brand)"
            class="font-weight-bold notif-action-btn"
            :disabled="totalNaoLidas === 0"
            @click="marcarTodasLidas"
          >
            <v-icon small class="mr-1">mdi-check-all</v-icon>
            Lidas todas
          </v-btn>
          <v-spacer />
          <v-btn
            text
            x-small
            color="var(--text-muted)"
            class="font-weight-bold notif-action-btn"
            :disabled="notificacoes.length === 0"
            @click="limparTodas"
          >
            <v-icon small class="mr-1">mdi-trash-can-outline</v-icon>
            Limpar tudo
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-menu>
  </div>
</template>

<script>
import { mapGetters } from "vuex";

export default {
  name: "ENotificacoesList",
  data() {
    return {
      menuAberto: false,
      notificacoes: [],
      totalNaoLidas: 0,
      carregando: false,
      intervalId: null,
    };
  },
  computed: {
    ...mapGetters("Auth", ["user"]),
  },
  watch: {
    user: {
      immediate: true,
      handler(val) {
        if (val) {
          this.carregar();
          this.iniciarIntervalo();
          this.solicitarPermissaoNotificacao();
        } else {
          this.pararIntervalo();
        }
      },
    },
    menuAberto(val) {
      if (val) {
        this.carregar();
      }
    },
  },
  destroyed() {
    this.pararIntervalo();
  },
  methods: {
    async carregar() {
      if (!this.user) return;
      try {
        this.carregando = true;
        const res = await this.$services.notificacoesService.listarMinhasNotificacoes();
        const novasNotificacoes = res.data || [];

        if (this.notificacoes.length > 0) {
          novasNotificacoes.forEach((n) => {
            if (
              n.status_view === "NAO_LIDA" &&
              !this.notificacoes.some((prev) => prev.id === n.id)
            ) {
              this.dispararNotificacaoNativa(n);
            }
          });
        }

        this.notificacoes = novasNotificacoes;
        this.totalNaoLidas = res.meta?.nao_lidas || 0;
      } catch (error) {
        this.$handleError(error);
      } finally {
        this.carregando = false;
      }
    },
    iniciarIntervalo() {
      this.pararIntervalo();
      this.intervalId = setInterval(() => {
        this.carregar();
      }, 10000);
    },
    pararIntervalo() {
      if (this.intervalId) {
        clearInterval(this.intervalId);
        this.intervalId = null;
      }
    },
    solicitarPermissaoNotificacao() {
      if ("Notification" in window && Notification.permission === "default") {
        Notification.requestPermission();
      }
    },
    dispararNotificacaoNativa(n) {
      if (!("Notification" in window) || Notification.permission !== "granted") {
        return;
      }

      const noChatPage = this.$route.name !== "chat.conversas";
      const tabOculta = document.hidden;

      if (tabOculta || noChatPage) {
        const titulo = n.titulo || "Nova Notificação";
        const opcoes = {
          body: n.mensagem || "",
          icon: "/favicon.ico",
        };

        try {
          const notification = new Notification(titulo, opcoes);
          this.tocarSomNotificacao();
          notification.onclick = () => {
            window.focus();
            this.clicarNotificacao(n);
          };
        } catch (e) {
          /* eslint-disable-next-line no-console */
          console.error("Erro ao exibir notificação nativa:", e);
        }
      }
    },
    tocarSomNotificacao() {
      try {
        const AudioContextClass = window.AudioContext || window.webkitAudioContext;
        if (!AudioContextClass) return;

        const audioCtx = new AudioContextClass();

        const playBeep = (delay, frequency, duration) => {
          const osc = audioCtx.createOscillator();
          const gain = audioCtx.createGain();

          osc.type = "sine";
          osc.frequency.setValueAtTime(frequency, audioCtx.currentTime + delay);

          gain.gain.setValueAtTime(0.08, audioCtx.currentTime + delay);
          gain.gain.exponentialRampToValueAtTime(0.0001, audioCtx.currentTime + delay + duration);

          osc.connect(gain);
          gain.connect(audioCtx.destination);

          osc.start(audioCtx.currentTime + delay);
          osc.stop(audioCtx.currentTime + delay + duration);
        };

        playBeep(0, 1046.5, 0.15);
        playBeep(0.12, 1318.51, 0.2);
      } catch (e) {
        /* eslint-disable-next-line no-console */
        console.error("Erro ao reproduzir som de notificação:", e);
      }
    },
    async clicarNotificacao(item) {
      try {
        if (item.status_view === "NAO_LIDA") {
          await this.$services.notificacoesService.visualizarNotificacao(item.id);
          this.carregar();
        }

        // Navigate based on type
        if (item.tipo === "falta_justificada") {
          this.$router.push({ name: "colaboradores.faltasJustificadas.gerenciar" }).catch(() => {});
        } else if (item.tipo === "falta_justificada_avaliacao") {
          this.$router.push({ name: "colaboradores.faltasJustificadas" }).catch(() => {});
        } else if (item.tipo === "batida_remota") {
          this.$router.push({ name: "colaboradores.batidasRemotas.gerenciar" }).catch(() => {});
        } else if (item.tipo === "batida_remota_avaliacao") {
          this.$router.push({ name: "colaboradores.batidasRemotas" }).catch(() => {});
        } else if (item.tipo === "chat_mensagem") {
          this.$router
            .push({ name: "chat.conversas", query: { conversaId: item.referencia_id } })
            .catch(() => {});
        } else if (item.tipo === "atraso_ponto" || item.tipo === "atraso_justificado") {
          this.$router
            .push({ name: "colaboradores.atrasosJustificados.gerenciar" })
            .catch(() => {});
        } else if (item.tipo === "atraso_justificado_avaliacao") {
          this.$router.push({ name: "colaboradores.atrasosJustificados" }).catch(() => {});
        }
        this.menuAberto = false;
      } catch (error) {
        this.$handleError(error);
      }
    },
    async excluirNotificacao(id) {
      try {
        await this.$services.notificacoesService.excluirNotificacao(id);
        this.carregar();
      } catch (error) {
        this.$handleError(error);
      }
    },
    async marcarTodasLidas() {
      try {
        await this.$services.notificacoesService.marcarTodasComoLidas();
        this.carregar();
      } catch (error) {
        this.$handleError(error);
      }
    },
    async limparTodas() {
      try {
        await this.$services.notificacoesService.excluirTodasNotificacoes();
        this.carregar();
      } catch (error) {
        this.$handleError(error);
      }
    },
    formatarDataHora(data) {
      if (!data) return "";
      return new Date(data).toLocaleString("pt-BR", {
        day: "2-digit",
        month: "2-digit",
        hour: "2-digit",
        minute: "2-digit",
      });
    },
    notifIcon(tipo) {
      switch (tipo) {
        case "falta_justificada":
        case "atraso_justificado":
          return "mdi-alert-circle-outline";
        case "falta_justificada_avaliacao":
        case "atraso_justificado_avaliacao":
        case "batida_remota_avaliacao":
          return "mdi-clipboard-check-outline";
        case "batida_remota":
          return "mdi-clock-check-outline";
        case "chat_mensagem":
          return "mdi-message-text-outline";
        case "atraso_ponto":
          return "mdi-timer-alert-outline";
        default:
          return "mdi-bell-outline";
      }
    },
    notifIconCls(tipo) {
      switch (tipo) {
        case "falta_justificada":
        case "falta_justificada_avaliacao":
          return "tone-rose";
        case "batida_remota":
        case "batida_remota_avaliacao":
          return "tone-green";
        case "chat_mensagem":
          return "tone-blue";
        case "atraso_ponto":
        case "atraso_justificado":
        case "atraso_justificado_avaliacao":
          return "tone-orange";
        default:
          return "tone-primary";
      }
    },
  },
};
</script>

<style scoped>
.notif-bell-btn {
  background: rgba(255, 255, 255, 0.14) !important;
  border-radius: 10px;
  transition: background-color 0.18s ease, transform 0.18s ease;
}

.notif-bell-btn:hover {
  background: rgba(255, 255, 255, 0.28) !important;
  transform: scale(1.06);
}

.notif-bell-btn.bell-ring ::v-deep .v-icon {
  animation: bellRing 2.6s ease infinite;
  transform-origin: 50% 10%;
}

@keyframes bellRing {
  0%,
  100% {
    transform: rotate(0deg);
  }
  5% {
    transform: rotate(12deg);
  }
  10% {
    transform: rotate(-10deg);
  }
  15% {
    transform: rotate(8deg);
  }
  20% {
    transform: rotate(-6deg);
  }
  25% {
    transform: rotate(0deg);
  }
}

::v-deep .notif-badge .v-badge__badge {
  top: 0px !important;
  right: 0px !important;
  border: 2px solid #6d28d9;
  font-weight: 700;
}
</style>

<style>
/* ===== PAINEL DE NOTIFICAÇÕES ===== */
.notif-panel {
  border-radius: 16px !important;
  overflow: hidden;
  box-shadow: 0 18px 44px rgba(79, 0, 143, 0.16) !important;
  border: 1px solid var(--border);
}

.notif-panel .notif-card {
  border-radius: 0;
}

.notif-panel .notif-header {
  background: linear-gradient(135deg, #4f008f 0%, #7c3aed 100%);
  color: #ffffff;
  padding: 14px 18px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  position: relative;
  overflow: hidden;
}

.notif-panel .notif-header::after {
  content: "";
  position: absolute;
  top: -40px;
  right: -20px;
  width: 160px;
  height: 130px;
  border-radius: 50%;
  background: radial-gradient(circle, rgba(255, 255, 255, 0.16), transparent 70%);
  pointer-events: none;
}

.notif-panel .notif-header-title {
  position: relative;
  z-index: 1;
  font-size: 15px;
  font-weight: 700;
  display: flex;
  align-items: center;
}

.notif-panel .notif-count-chip {
  position: relative;
  z-index: 1;
  background: linear-gradient(135deg, #d97706, #fbbf24);
  color: #737373;
  font-weight: 700;
}

.notif-panel .notif-header-divider {
  margin: 0;
  border-color: var(--border-soft);
}

.notif-panel .notif-list {
  max-height: 300px;
  overflow-y: auto;
}

.notif-panel .notif-list::-webkit-scrollbar {
  width: 6px;
}

.notif-panel .notif-list::-webkit-scrollbar-thumb {
  background: var(--brand-border);
  border-radius: 3px;
}

.notif-panel .notif-item {
  border-radius: 12px;
  margin: 2px 4px;
  cursor: pointer;
  position: relative;
  transition: transform 0.15s ease, box-shadow 0.15s ease, background-color 0.15s ease;
}

.notif-panel .notif-item:hover:not(.notif-item-unread) {
  background-color: var(--surface-2) !important;
}

.notif-panel .notif-item-unread {
  background: linear-gradient(135deg, var(--brand-soft), var(--surface-hover)) !important;
  border: 1px solid var(--brand-border);
}

.notif-panel .notif-item-unread:hover {
  transform: translateY(-1px);
  box-shadow: 0 6px 16px rgba(79, 0, 143, 0.08);
}

.notif-panel .notif-dot {
  position: absolute;
  left: 8px;
  top: 14px;
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: #7c3aed;
  box-shadow: 0 0 0 3px rgba(124, 58, 237, 0.18);
}

.notif-panel .notif-icon {
  width: 36px;
  height: 36px;
  border-radius: 11px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex: 0 0 auto;
}

.notif-panel .notif-icon.tone-primary {
  background: linear-gradient(135deg, #7c3aed, #a78bfa);
}

.notif-panel .notif-icon.tone-rose {
  background: linear-gradient(135deg, #e11d48, #fb7185);
}

.notif-panel .notif-icon.tone-amber {
  background: linear-gradient(135deg, #d97706, #fbbf24);
}

.notif-panel .notif-icon.tone-blue {
  background: linear-gradient(135deg, #0284c7, #38bdf8);
}

.notif-panel .notif-icon.tone-green {
  background: linear-gradient(135deg, #059669, #34d399);
}

.notif-panel .notif-icon.tone-orange {
  background: linear-gradient(135deg, #ea580c, #fb923c);
}

.notif-panel .notif-title {
  color: var(--text);
}

.notif-panel .notif-message {
  color: var(--text-muted);
}

.notif-panel .notif-date {
  color: var(--text-faint);
}

.notif-panel .notif-delete-btn {
  opacity: 0.45;
  transition: opacity 0.15s ease, background-color 0.15s ease;
}

.notif-panel .notif-delete-btn:hover {
  opacity: 1;
  background-color: rgba(239, 68, 68, 0.15);
}

.notif-panel .notif-empty-icon {
  width: 64px;
  height: 64px;
  margin: 0 auto 12px;
  border-radius: 18px;
  background: var(--brand-soft-2);
  display: flex;
  align-items: center;
  justify-content: center;
}

.notif-panel .notif-empty-text {
  color: var(--text-faint);
  font-weight: 500;
  font-size: 14px;
}

.notif-panel .notif-action-btn {
  border-radius: 8px;
}
</style>
